import React, { useState } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { Dialog } from '@mui/material'
import { toast } from 'react-toastify'
import * as estateDocumentsService from 'services/estateDocumentsService'
import { loadEstateOwnedFilesRoutine, loadEstateFoldersRoutine } from 'ducks/estateDocuments/actions'
import { font, ink, line, radius, surface } from 'features/dashboard/tokens'

const DANGER = '#b00020'

const EstateBulkDeleteFilesModal = ({ estateId, fileIds, folderId, onClose, onDeleted }) => {
  const dispatch = useDispatch()
  const [deleting, setDeleting] = useState(false)
  const count = fileIds.length

  const handleDelete = async () => {
    if (deleting) return
    setDeleting(true)

    const results = await Promise.allSettled(
      fileIds.map(fileId => estateDocumentsService.deleteEstateFile({ estateId, fileId }))
    )

    const succeeded = results.filter(result => result.status === 'fulfilled').length
    const failed = results.length - succeeded

    if (failed === 0) {
      toast.success(`Deleted ${succeeded} document${succeeded === 1 ? '' : 's'}.`)
    } else if (succeeded === 0) {
      toast.error(`Could not delete ${failed} document${failed === 1 ? '' : 's'}.`)
    } else {
      toast.warning(`${succeeded} of ${results.length} documents deleted, ${failed} failed.`)
    }

    dispatch(loadEstateOwnedFilesRoutine({ estateId, folderId, page: 1, reloadLoaded: true }))
    dispatch(loadEstateFoldersRoutine({ estateId }))
    setDeleting(false)
    onDeleted()
    onClose()
  }

  return (
    <Dialog open onClose={deleting ? undefined : onClose} maxWidth='xs' fullWidth>
      <Container>
        <Title>Delete {count} document{count === 1 ? '' : 's'}?</Title>
        <Body>
          These documents will be removed from the estate's documents and can no longer be viewed or downloaded. This cannot be undone here.
        </Body>
        <Footer>
          <CancelButton type='button' onClick={onClose} disabled={deleting}>
            Cancel
          </CancelButton>
          <DeleteButton type='button' onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : `Delete ${count} document${count === 1 ? '' : 's'}`}
          </DeleteButton>
        </Footer>
      </Container>
    </Dialog>
  )
}

export default EstateBulkDeleteFilesModal

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 24px;
  background: ${surface.card};
`

const Title = styled.h2`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 700;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Body = styled.p`
  margin: 0;
  font-size: ${font.body};
  line-height: 1.5;
  color: ${ink.soft};
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const CancelButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: color 160ms ease;

  &:hover:not(:disabled) {
    color: ${ink.base};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const DeleteButton = styled.button`
  min-height: 40px;
  padding: 0 20px;
  border: none;
  border-radius: ${radius.sm};
  background: ${DANGER};
  color: #ffffff;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: opacity 160ms ease;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`
